import type { SystemId, SystemInfo, PadButton } from './types';

const DPAD: PadButton[] = ['UP', 'DOWN', 'LEFT', 'RIGHT'];

export const SYSTEMS: Record<SystemId, SystemInfo> = {
  gb: {
    id: 'gb',
    name: 'Game Boy / Color',
    short: 'GB',
    nativeWidth: 160,
    nativeHeight: 144,
    aspect: 160 / 144,
    fps: 59.7275,
    extensions: ['gb', 'gbc', 'sgb'],
    requiresBios: false,
    acceptsBios: false,
    buttons: [...DPAD, 'A', 'B', 'START', 'SELECT'],
  },
  gba: {
    id: 'gba',
    name: 'Game Boy Advance',
    short: 'GBA',
    nativeWidth: 240,
    nativeHeight: 160,
    // GBA は正方ピクセルなので実解像度そのままが正しい比率
    aspect: 240 / 160,
    fps: 59.7275,
    extensions: ['gba', 'agb'],
    // mGBA は BIOS を HLE で代替できるので必須ではない。
    // 実機 BIOS を入れると起動ロゴなど一部の挙動がより正確になる。
    requiresBios: false,
    acceptsBios: true,
    buttons: [...DPAD, 'A', 'B', 'L', 'R', 'START', 'SELECT'],
  },
  nes: {
    id: 'nes',
    name: 'Nintendo Entertainment System',
    short: 'NES',
    nativeWidth: 256,
    nativeHeight: 240,
    // NES は 8:7 ピクセルアスペクト → 表示は 4:3 相当
    aspect: 4 / 3,
    fps: 60.0988,
    extensions: ['nes', 'fds', 'unf', 'unif'],
    requiresBios: false,
    acceptsBios: false,
    buttons: [...DPAD, 'A', 'B', 'START', 'SELECT'],
  },
  snes: {
    id: 'snes',
    name: 'Super Nintendo',
    short: 'SNES',
    nativeWidth: 256,
    nativeHeight: 224,
    aspect: 4 / 3,
    fps: 60.0988,
    extensions: ['sfc', 'smc', 'fig', 'swc', 'bs'],
    requiresBios: false,
    acceptsBios: false,
    buttons: [...DPAD, 'A', 'B', 'X', 'Y', 'L', 'R', 'START', 'SELECT'],
  },
  psx: {
    id: 'psx',
    name: 'PlayStation',
    short: 'PS1',
    nativeWidth: 320,
    nativeHeight: 240,
    aspect: 4 / 3,
    fps: 59.94,
    // .iso / .pbp は PSP と紛らわしいのでヘッダ優先（detectSystem）
    extensions: ['bin', 'cue', 'iso', 'img', 'chd', 'ecm', 'mdf', 'm3u', 'exe'],
    requiresBios: true,
    acceptsBios: true,
    buttons: [...DPAD, 'A', 'B', 'X', 'Y', 'L', 'R', 'L2', 'R2', 'START', 'SELECT'],
  },
  ws: {
    id: 'ws',
    name: 'WonderSwan / Color',
    short: 'WS',
    nativeWidth: 224,
    nativeHeight: 144,
    aspect: 224 / 144,
    fps: 75.47,
    extensions: ['ws', 'wsc', 'pc2'],
    requiresBios: false,
    acceptsBios: false,
    buttons: [...DPAD, 'A', 'B', 'START', 'SELECT'],
  },
  psp: {
    id: 'psp',
    name: 'PlayStation Portable',
    short: 'PSP',
    nativeWidth: 480,
    nativeHeight: 272,
    aspect: 480 / 272,
    fps: 59.94,
    extensions: ['iso', 'cso', 'pbp', 'prx', 'elf'],
    requiresBios: false,
    acceptsBios: false,
    buttons: [...DPAD, 'A', 'B', 'X', 'Y', 'L', 'R', 'START', 'SELECT'],
  },
};

export const SYSTEM_LIST: SystemInfo[] = Object.values(SYSTEMS);

/** 全機種の拡張子 → SystemId 逆引き */
const EXT_MAP: Map<string, SystemId> = (() => {
  const m = new Map<string, SystemId>();
  for (const sys of SYSTEM_LIST) {
    for (const ext of sys.extensions) m.set(ext, sys.id);
  }
  return m;
})();

export const ALL_EXTENSIONS: string[] = [...EXT_MAP.keys()];

export function extensionOf(filename: string): string {
  const i = filename.lastIndexOf('.');
  return i < 0 ? '' : filename.slice(i + 1).toLowerCase();
}

export function systemFromExtension(filename: string): SystemId | null {
  return EXT_MAP.get(extensionOf(filename)) ?? null;
}

/**
 * 拡張子が当てにならない場合（.bin が PS1 とも限らない等）のヘッダ判定。
 * 拡張子判定より優先度は低いが、両方使うと誤判定がかなり減る。
 */
function hasAscii(bytes: Uint8Array, needle: string, limit = 256 * 1024): boolean {
  const n = Math.min(bytes.length, limit);
  const target = needle.length;
  outer: for (let i = 0; i <= n - target; i++) {
    for (let j = 0; j < target; j++) {
      if (bytes[i + j] !== needle.charCodeAt(j)) continue outer;
    }
    return true;
  }
  return false;
}

export function systemFromHeader(bytes: Uint8Array): SystemId | null {
  // GBA: 0x04 から 156 バイトの Nintendo ロゴ、0xB2 に固定値 0x96。
  // GB より先に判定する。SNES のチェックサム推定に誤って拾われるのも防げる。
  if (bytes.length > 0xb2 && bytes[0xb2] === 0x96) {
    const GBA_LOGO_HEAD = [0x24, 0xff, 0xae, 0x51, 0x69, 0x9a, 0xa2, 0x21];
    let gbaMatch = true;
    for (let i = 0; i < GBA_LOGO_HEAD.length; i++) {
      if (bytes[0x04 + i] !== GBA_LOGO_HEAD[i]) {
        gbaMatch = false;
        break;
      }
    }
    if (gbaMatch) return 'gba';
  }

  // iNES: "NES\x1a"
  if (bytes.length > 4 && bytes[0] === 0x4e && bytes[1] === 0x45 && bytes[2] === 0x53 && bytes[3] === 0x1a) {
    return 'nes';
  }

  // Game Boy: 0x0104-0x0133 に固定の Nintendo ロゴ
  if (bytes.length > 0x134) {
    const LOGO_HEAD = [0xce, 0xed, 0x66, 0x66, 0xcc, 0x0d, 0x00, 0x0b];
    let match = true;
    for (let i = 0; i < LOGO_HEAD.length; i++) {
      if (bytes[0x104 + i] !== LOGO_HEAD[i]) {
        match = false;
        break;
      }
    }
    if (match) return 'gb';
  }

  // PSP: PBP コンテナ (\0PBP)
  if (
    bytes.length > 4 &&
    bytes[0] === 0x00 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x42 &&
    bytes[3] === 0x50
  ) {
    return 'psp';
  }

  // PSP ISO / CSO 内のディレクトリ名
  if (hasAscii(bytes, 'PSP_GAME')) return 'psp';

  // PS1 トラック先頭 (2352 バイトセクタ) の同期パターン
  if (bytes.length > 16) {
    const SYNC = [0x00, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0x00];
    let match = true;
    for (let i = 0; i < SYNC.length; i++) {
      if (bytes[i] !== SYNC[i]) {
        match = false;
        break;
      }
    }
    if (match) return 'psx';
  }

  // ISO9660 (2048 バイトセクタ): 0x8001 に "CD001" → PS1 とみなす（PSP は上で弾く）
  if (bytes.length > 0x8006) {
    const cd001 = String.fromCharCode(...bytes.subarray(0x8001, 0x8006));
    if (cd001 === 'CD001') return 'psx';
  }

  // SNES はマジックナンバーが無いのでチェックサム補数で推定する。
  // LoROM(0x7fdc) / HiROM(0xffdc) のヘッダ位置を総当たり。
  for (const base of [0x7fc0, 0xffc0, 0x40ffc0]) {
    const cs = base + 0x1c;
    if (bytes.length < cs + 4) continue;
    const comp = (bytes[cs] ?? 0) | ((bytes[cs + 1] ?? 0) << 8);
    const sum = (bytes[cs + 2] ?? 0) | ((bytes[cs + 3] ?? 0) << 8);
    if (comp !== 0 && sum !== 0 && (comp ^ sum) === 0xffff) return 'snes';
  }

  return null;
}

/** 拡張子とヘッダを合わせて機種を決定する。 */
export function detectSystem(filename: string, bytes: Uint8Array): SystemId | null {
  const byExt = systemFromExtension(filename);
  const byHeader = systemFromHeader(bytes);

  // .bin / .img / .iso は多機種で使われるためヘッダを優先
  const ambiguous = ['bin', 'img', 'rom', 'zip', 'iso'];
  if (byExt && ambiguous.includes(extensionOf(filename))) {
    return byHeader ?? byExt;
  }
  return byExt ?? byHeader;
}
